import { useResendLoginEmailMutation, userQuery } from "@common/api/users"
import { ConfirmDialog } from "@common/components/shared/confirm-dialog"
import { useReAuthGuard } from "@common/hooks/use-reauth-guard"
import { handleError } from "@common/utils"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, Navigate, useNavigate } from "@tanstack/react-router"
import useCustomToast from "@/hooks/use-custom-toast"
import { displayName } from "../../-lib/display-name"

export const Route = createFileRoute(
  "/_layout/settings/_panel/workspace/members/_list/$userId/resend-login-email",
)({
  component: ResendLoginEmailDialog,
})

function ResendLoginEmailDialog(): React.ReactElement {
  const { userId } = Route.useParams()
  const { data: user } = useSuspenseQuery(userQuery({ user_id: userId }))
  const navigate = useNavigate()
  const showToast = useCustomToast()
  const resendMutation = useResendLoginEmailMutation()
  const { guard, handleFreshnessError, reAuthModal } = useReAuthGuard()
  const name = displayName(user)

  const close = () =>
    navigate({
      to: "/settings/workspace/members/$userId",
      params: { userId },
      search: true,
    })

  if (!user.email) {
    return (
      <Navigate
        to="/settings/workspace/members/$userId"
        params={{ userId }}
        search={true}
      />
    )
  }

  const confirm = () => {
    const send = () =>
      resendMutation.mutate(
        { user_id: userId },
        {
          onSuccess: () => {
            showToast(
              "Password reset",
              `Login email sent to ${user.email}.`,
              "success",
            )
            close()
          },
          onError: (error) => {
            if (handleFreshnessError(error, send)) return
            handleError(error, showToast)
          },
        },
      )
    guard(send)
  }

  return (
    <>
      <ConfirmDialog
        title={`Reset password for ${name}?`}
        description={`Their current password stops working. A new temporary password is emailed to ${user.email}, and they must change it when they sign in.`}
        action="Reset password"
        tone="default"
        pendingAction="Resetting…"
        pending={resendMutation.isPending}
        onConfirm={confirm}
        onCancel={close}
      />
      {reAuthModal}
    </>
  )
}
